import { Input, Select, Button } from '@cloudflare/kumo'
import { XIcon } from '@phosphor-icons/react'
import type { PortalAccount, PortalServer } from '../types'

export type StatusFilter = 'all' | 'broken' | 'blocked' | 'error' | 'degraded' | 'ok'

export interface Filters {
  query: string
  serverId: string
  accountId: string
  status: StatusFilter
}

export const EMPTY_FILTERS: Filters = {
  query: '',
  serverId: 'all',
  accountId: 'all',
  status: 'all',
}

function isActive(f: Filters): boolean {
  return (
    f.query.trim() !== '' ||
    f.serverId !== 'all' ||
    f.accountId !== 'all' ||
    f.status !== 'all'
  )
}

export function FilterBar({
  filters,
  onChange,
  servers,
  accounts,
}: {
  filters: Filters
  onChange: (f: Filters) => void
  servers: PortalServer[]
  accounts: PortalAccount[]
}) {
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    onChange({ ...filters, [key]: value })

  return (
    <div className="flex flex-wrap items-end gap-3 mb-4">
      <div className="min-w-[220px] flex-1">
        <Input
          aria-label="Search servers or customers"
          placeholder="Search servers or customers…"
          value={filters.query}
          onChange={(e) => set('query', e.target.value)}
        />
      </div>

      <Select
        label="Server"
        hideLabel={false}
        value={filters.serverId}
        onValueChange={(v) => set('serverId', v ?? 'all')}
      >
        <Select.Option value="all">All servers</Select.Option>
        {servers.map((s) => (
          <Select.Option key={s.server_id} value={s.server_id}>
            {s.server_name}
          </Select.Option>
        ))}
      </Select>

      <Select
        label="Customer"
        hideLabel={false}
        value={filters.accountId}
        onValueChange={(v) => set('accountId', v ?? 'all')}
      >
        <Select.Option value="all">All customers</Select.Option>
        {accounts.map((a) => (
          <Select.Option key={a.account_id} value={a.account_id}>
            {a.account_name}
          </Select.Option>
        ))}
      </Select>

      <Select
        label="Status"
        hideLabel={false}
        value={filters.status}
        onValueChange={(v) => set('status', (v ?? 'all') as StatusFilter)}
      >
        <Select.Option value="all">All statuses</Select.Option>
        <Select.Option value="broken">Only broken</Select.Option>
        <Select.Option value="blocked">Blocked</Select.Option>
        <Select.Option value="error">Error</Select.Option>
        <Select.Option value="degraded">Degraded</Select.Option>
        <Select.Option value="ok">OK</Select.Option>
      </Select>

      {isActive(filters) && (
        <Button
          variant="ghost"
          icon={<XIcon />}
          onClick={() => onChange(EMPTY_FILTERS)}
        >
          Clear
        </Button>
      )}
    </div>
  )
}
